import { Alert, Button, Spin } from "@arco-design/web-react";

import { useFinanceOverviewWorkbenchRuntime } from "../runtime/useFinanceOverviewWorkbenchRuntime";
import {
  financeWorkbenchTargetPath,
  formatFinancePayableAmount,
  formatFinanceReceivableAmount,
  type FinanceWorkbenchTarget,
} from "../service";
import "./financeOverview.css";

type Props = {
  /** 跳到给定地址；卡片、待办点进去的地址由本页按目标页读写地址的同一套函数拼好（带好筛选）。 */
  onNavigate: (path: string) => void;
};

/** 团太多、这一项算不出来时写的话：不给截断的数、不拿 0 顶替，指去对应列表加出团日期范围。 */
const LIMIT_EXCEEDED_TEXT = "团太多，算不出来";

/** 工作台上的一项（卡片或待办）：名字、数、一句说明，有入口时再放一个按钮；读屏和测试按名字找它。 */
function WorkbenchItem({
  name,
  value,
  exceeded,
  note,
  action,
}: {
  name: string;
  value: string;
  exceeded: boolean;
  note: string;
  action: { label: string; onClick: () => void } | null;
}) {
  return (
    <div className="finance-workbench-item" role="group" aria-label={name}>
      <span className="finance-workbench-name">{name}</span>
      <strong className={exceeded ? "finance-workbench-value finance-workbench-value--exceeded" : "finance-workbench-value"}>
        {value}
      </strong>
      <p className="finance-workbench-note">{note}</p>
      {action ? (
        <div className="finance-workbench-actions">
          <Button size="small" onClick={action.onClick}>
            {action.label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 财务工作台：三张总数卡（应收总额、已收、真实应付）、三类待办的件数（轮到我审批的、审批通过还没登记已付的、
 * 还有可请额度的团）。待办点进去是已有的页面、带好筛选；三张卡只显示金额。没有那一页权限的项接口不给，
 * 这里整个不显示；全部团超过候选上限、算不出来的项写明算不出来，给去对应列表加出团日期范围的入口，不显示 0。
 * 不做原型里的「重点团队收付状态」「正常收付数据链」「导游报账」。
 */
export function FinanceOverviewWorkbenchFeaturePage({ onNavigate }: Props) {
  const runtime = useFinanceOverviewWorkbenchRuntime();
  const onOpen = (target: FinanceWorkbenchTarget) => onNavigate(financeWorkbenchTargetPath(target));
  const data = runtime.data;
  const limitNote = (listName: string) => `全部团超过候选上限，请到${listName}加出团日期范围分批看。`;
  // 值为空只会是算不出来；万一标记和值对不上，也按算不出来显示，不拿 0 顶替。
  const receivable = data?.receivableTotal;
  const receivableExceeded = Boolean(receivable && (receivable.candidateLimitExceeded || receivable.amount === null));
  const received = data?.receivedTotal;
  const receivedExceeded = Boolean(received && (received.candidateLimitExceeded || received.amount === null));
  const payable = data?.itineraryPayableTotal;
  const payableExceeded = Boolean(payable && (payable.candidateLimitExceeded || payable.amount === null));
  const requestable = data?.requestableItineraryCount;
  const requestableExceeded = Boolean(requestable && (requestable.candidateLimitExceeded || requestable.count === null));
  return (
    <main className="finance-overview-page finance-workbench-page" aria-label="财务工作台" aria-busy={runtime.loading}>
      {runtime.failed ? (
        <Alert
          className="finance-overview-alert"
          type="error"
          content="财务工作台加载失败，请重试。"
          action={<Button size="mini" onClick={runtime.refresh}>重试</Button>}
        />
      ) : null}
      {runtime.loading && !data ? <Spin className="finance-workbench-loading" /> : null}
      {data && !runtime.failed ? (
        <>
          {receivable || received || payable ? (
            <section className="finance-workbench-section" aria-label="总数">
              <div className="finance-workbench-grid">
                {receivable ? (
                  <WorkbenchItem
                    name="应收总额"
                    value={receivableExceeded ? LIMIT_EXCEEDED_TEXT : formatFinanceReceivableAmount(receivable.amount)}
                    exceeded={receivableExceeded}
                    note={
                      receivableExceeded
                        ? limitNote("应收列表")
                        : "全部团的当前个人应收合计，名单未定稿的不算。"
                    }
                    action={receivableExceeded ? { label: "去应收列表", onClick: () => onOpen("receivableList") } : null}
                  />
                ) : null}
                {received ? (
                  <WorkbenchItem
                    name="已收"
                    value={receivedExceeded ? LIMIT_EXCEEDED_TEXT : formatFinanceReceivableAmount(received.amount)}
                    exceeded={receivedExceeded}
                    note={
                      receivedExceeded
                        ? limitNote("应收列表")
                        : "只算通过本系统团专属二维码、银行卡收到的钱；团款目前大多还在旧系统收，这里会偏少。"
                    }
                    action={receivedExceeded ? { label: "去应收列表", onClick: () => onOpen("receivableList") } : null}
                  />
                ) : null}
                {payable ? (
                  <WorkbenchItem
                    name="真实应付"
                    value={payableExceeded ? LIMIT_EXCEEDED_TEXT : formatFinancePayableAmount(payable.amount)}
                    exceeded={payableExceeded}
                    note={
                      payableExceeded
                        ? limitNote("应付列表")
                        : "全部行程的真实应付合计，含未归口金额。"
                    }
                    action={payableExceeded ? { label: "去应付列表", onClick: () => onOpen("payableList") } : null}
                  />
                ) : null}
              </div>
            </section>
          ) : null}
          <section className="finance-workbench-section" aria-label="待办">
            <h2 className="finance-workbench-heading">待办</h2>
            <div className="finance-workbench-grid">
              <WorkbenchItem
                name="轮到我审批的"
                value={`${data.pendingApprovalCount} 张`}
                exceeded={false}
                note="当前这一级轮到我审批的单子。"
                action={{ label: "去处理", onClick: () => onOpen("approvalTodo") }}
              />
              {data.awaitingPaymentCount !== null ? (
                <WorkbenchItem
                  name="审批通过、还没登记已付的"
                  value={`${data.awaitingPaymentCount} 张`}
                  exceeded={false}
                  note="请款记录里的「待付款」。"
                  action={{ label: "去处理", onClick: () => onOpen("awaitingPayment") }}
                />
              ) : null}
              {requestable ? (
                <WorkbenchItem
                  name="还有可请额度的团"
                  value={requestableExceeded ? LIMIT_EXCEEDED_TEXT : `${requestable.count} 个`}
                  exceeded={requestableExceeded}
                  note={requestableExceeded ? limitNote("应付列表") : "有一家结算主体的可请额度大于 0。"}
                  action={{
                    label: requestableExceeded ? "去应付列表" : "去处理",
                    onClick: () => onOpen("requestableItineraries"),
                  }}
                />
              ) : null}
            </div>
          </section>
        </>
      ) : null}
    </main>
  );
}
